/** Joining a workspace with an invitation token — **ADR-0046's browser half**.
 *
 * Until this existed the backend could issue a claim and redeem one, and a person holding a token
 * had nowhere to use it. Five things about this screen are the point of it.
 *
 * **One message for every refusal.** The API answers an unknown token, a wrong address, an expired
 * claim, a used one and a revoked one with the same 404 and the same body, so the screen has one
 * sentence for all of them and **renders nothing from the response**. A helpful "that token has
 * expired" would be an oracle the API deliberately is not: it would confirm the token was real.
 * What *is* told apart is a request that did not complete — a different fact with a different
 * remedy, and not the person's fault.
 *
 * **Nothing is disclosed before the pair is proved.** The first step asks for the token *and* the
 * address it was issued to, and says nothing about any workspace until the API has accepted both.
 * Only then does it name the organisation and the role, which is how a person tells a genuine
 * invitation from a phishing page. Preview and redemption are separate presses on purpose: showing
 * the workspace and joining it in one step would remove the only moment to say "I did not expect
 * this".
 *
 * **A new person chooses a password; an existing one is not asked for one.** `claims.redeem`
 * ignores a password for an address that already has an account, as a security property — a claim
 * that set one would let anybody holding a token for any organisation reset the credential of any
 * address they could guess. So the form does not offer a field that would do nothing, and the
 * request does not carry a `password` key at all on that path. The new-person form asks twice,
 * because the token is single use: a mistyped password is not something a second attempt can fix.
 *
 * **Joining is not signing in.** Redemption returns no session (`test_redeeming_does_not_hand_
 * back_a_session`), because an Admin must pass a second factor first. The last step therefore
 * sends the person to the sign-in form and says why, rather than appearing to have signed them in.
 * This screen never calls `/auth/me` or `/auth/login`.
 *
 * **The token lives in memory and nowhere else.** Not in the address, where it would reach history,
 * logs and `Referer`; not in storage; not in the mutation cache (`gcTime: 0`); and the fields are
 * cleared the moment the redemption succeeds. The link format is *not* a decision this screen
 * makes — see the handoff in the change that added it.
 */

import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import type { ClaimPreview } from "../api/claims";
import { usePreviewClaim, useRedeemClaim } from "../api/claims";
import { AuthCard, AuthField, AuthNotice, AuthPage } from "../components/AuthLayout";
import { withArticle } from "../lib/article";
import { useDocumentTitle } from "../shell/documentTitle";
import styles from "../components/AuthLayout.module.css";

type Stage = "lookup" | "confirm" | "done";

/** The id a notice carries, so the field it is about can point at it. */
const NOTICE_ID = "join-notice";

/** What went wrong, when something did. Each is a different fact:
 *
 * - `refused` — the API said no, in the one way it ever says no. Says nothing about *why*.
 * - `unreachable` — the request did not complete, or the answer was not the API's. Nothing was
 *   changed, and the same details may well work next time.
 * - `unconfirmed` — **only for redemption**, where an interrupted request may have been carried out
 *   after the connection dropped. The token may now be spent, so "try again" is not the safe
 *   advice and "sign in" is.
 * - `mismatch` — the two password boxes differ. Local; nothing was sent.
 * - `name_missing` — the name box holds only spaces. Local; nothing was sent. The server needs at
 *   least one character, and a field that is `required` is satisfied by a space.
 * - `invalid` — the server refused the *shape* of the request (a 422 that is not the password
 *   rule). Validation runs before the route, so nothing was redeemed and the token is not spent;
 *   the remedy is to correct a field, never "sign in first".
 */
type Notice =
  | "refused"
  | "unreachable"
  | "unconfirmed"
  | "mismatch"
  | "too_weak"
  | "name_missing"
  | "invalid"
  | null;

/** The server's rule, mirrored so it can be **said before somebody types** and checked before a
 * single-use token is spent on a password that will be refused.
 *
 * **The server is the authority** (`passwords.MIN_LENGTH`) and refuses regardless; this is a
 * pre-check and a sentence, not a second rule. A copy of a number is a thing this product
 * normally refuses to keep, so the drift is named: if the server's minimum rises above this,
 * the API answers `password_too_weak` and the screen shows this same notice — actionable, with
 * a sentence understating the rule by a character or two. If it falls below, the screen is
 * merely stricter than it needs to be. Neither can produce the 2026-10-16 failure, which was a
 * refusal with **no** sentence at all. */
const MIN_PASSWORD_LENGTH = 12;

/** The bounds the API validates a lookup against (`ClaimLookupIn`), mirrored for the same reason
 * as the password minimum: a request outside them is a 422 that says nothing a refusal does not,
 * and a 422 reaching the preview call would be reported as "EmitIQ could not be reached" — blaming
 * our availability for a token somebody pasted wrongly. Outside them the answer is the one refusal
 * sentence, sent nowhere. The server remains the authority; this only decides what is worth asking. */
const TOKEN_LENGTH = { min: 8, max: 512 } as const;
/** No minimum for the address: the input is `type="email"`, so the browser already refuses anything
 * shorter than `a@b` before a submit reaches this code, and a bound that cannot be reached cannot be
 * tested. */
const EMAIL_MAX_LENGTH = 320;
/** `ClaimRedeemIn.display_name`: 1 to 200 characters. */
const NAME_MAX_LENGTH = 200;

/** Roles that always need a second factor (ADR-0008, `mfa_policy`). Used only to say so on the last
 * step, so a new Admin is not surprised by an enrolment screen with a five-minute clock. */
const ALWAYS_MFA: ReadonlySet<string> = new Set(["admin", "approver"]);

/** The one sentence a refusal produces. Deliberately lists the causes the server's own message
 * lists and no more: naming them all is not an oracle, and naming *one* would be. */
const REFUSED =
  "This invitation could not be used. It may have expired, already been used, or been issued for " +
  "a different email address. Check the token and the address, and if it still does not work, " +
  "ask whoever invited you to issue a new one.";

/** "admin" → "Admin". Derived rather than tabled: the role list and what each role may do belong
 * to the People screen and the API, and a second copy here would be the one that drifts. */
function roleWord(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function JoinScreen() {
  const [stage, setStage] = useState<Stage>("lookup");
  useDocumentTitle("Join your workspace");
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [notice, setNotice] = useState<Notice>(null);
  const [preview, setPreview] = useState<ClaimPreview | null>(null);
  const [joined, setJoined] = useState<{
    organizationName: string;
    existing: boolean;
    role: string;
  } | null>(null);

  const previewClaim = usePreviewClaim();
  const redeemClaim = useRedeemClaim();

  /** A token is single use, so a second request while the first is in flight is at best a
   * guaranteed refusal that reads as the invitation being broken. `isPending` only flips after a
   * render; this is set in the same tick as the press. */
  const inFlight = useRef(false);

  // Focus follows the step. A keyboard or screen-reader user who presses the button is otherwise
  // left on a control that no longer exists. Compared with the previous stage rather than guarded
  // by a "first run" flag, which StrictMode's double-invoked effects defeat.
  const heading = useRef<HTMLHeadingElement>(null);
  const shown = useRef<Stage>(stage);
  useEffect(() => {
    if (shown.current !== stage) {
      shown.current = stage;
      heading.current?.focus();
    }
  }, [stage]);

  function changeDetails() {
    // The password goes; the token and the address stay, because re-typing a 43-character token
    // to fix one letter of an address is what makes people give up.
    setPassword("");
    setConfirm("");
    setPreview(null);
    setNotice(null);
    setStage("lookup");
  }

  async function submitLookup(event: React.FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    setNotice(null);
    const lookup = { token: token.trim(), email: email.trim() };
    // Outside the API's bounds, so it would answer 422, which says nothing a refusal does not. Said
    // here with the one sentence rather than sending a request that cannot succeed — and rather
    // than letting the 422 surface as "EmitIQ could not be reached".
    if (
      lookup.token.length < TOKEN_LENGTH.min ||
      lookup.token.length > TOKEN_LENGTH.max ||
      lookup.email.length > EMAIL_MAX_LENGTH
    ) {
      setNotice("refused");
      return;
    }
    inFlight.current = true;
    try {
      const result = await previewClaim.mutateAsync(lookup);
      if (result.status === "refused") {
        setNotice("refused");
        return;
      }
      // Confirmation and redemption must use the exact pair that produced this preview. An
      // input event during the request must not let us name one workspace and join another.
      // Keep the pair in the existing fields so the usual change-details/success cleanup applies.
      setToken(lookup.token);
      setEmail(lookup.email);
      setPreview({
        organizationName: result.organizationName,
        role: result.role,
        needsCredential: result.needsCredential,
      });
      setStage("confirm");
    } catch {
      setNotice("unreachable");
    } finally {
      inFlight.current = false;
    }
  }

  async function submitRedeem(event: React.FormEvent) {
    event.preventDefault();
    if (inFlight.current || !preview) return;
    setNotice(null);
    if (preview.needsCredential && displayName.trim() === "") {
      // `required` is satisfied by a space, the trimmed name is empty, and the server needs one
      // character: the request would be a 422 that this screen used to report as an interrupted
      // redemption. Checked before the single-use token is sent anywhere.
      setNotice("name_missing");
      return;
    }
    if (preview.needsCredential && password !== confirm) {
      // Checked before anything is sent: the token is single use, and a request that spends it on
      // a password the person did not mean to type cannot be taken back.
      setNotice("mismatch");
      return;
    }
    // **The same reason, one line later, and its absence cost a customer their first sign-in.**
    // The length rule lived only in `passwords.hash_password`, the redeem route did not catch what
    // it raises, and a short password was therefore a 500 that this screen reported as "we could
    // not confirm whether you joined" — which sent the person to sign in, the one remedy that
    // could not work. Checked here for the mismatch check's own reason: the token is single use.
    if (preview.needsCredential && password.length < MIN_PASSWORD_LENGTH) {
      setNotice("too_weak");
      return;
    }
    inFlight.current = true;
    try {
      const result = await redeemClaim.mutateAsync({
        token: token.trim(),
        email: email.trim(),
        // A server-required field that is ignored for an existing person. Sent as the address the
        // server already holds rather than an invented name; see the handoff.
        displayName: preview.needsCredential ? displayName.trim() : email.trim(),
        ...(preview.needsCredential ? { password } : {}),
      });
      if (result.status === "too_weak") {
        // The password is kept rather than cleared: it is the thing being corrected, and a form
        // that empties both boxes on a length complaint makes the person retype a password they
        // were about to lengthen. Nothing was spent — the server refused before redeeming.
        setNotice("too_weak");
        return;
      }
      if (result.status === "invalid") {
        // Kept as typed, like `too_weak`: the person is correcting something in this form, and
        // nothing was spent.
        setNotice("invalid");
        return;
      }
      if (result.status === "refused") {
        // Someone else got there first, or it expired while the page was open. Back to the start
        // with the one sentence — the organisation's name is not repeated after a refusal.
        setPassword("");
        setConfirm("");
        setPreview(null);
        setStage("lookup");
        setNotice("refused");
        return;
      }
      setJoined({
        organizationName: result.organizationName,
        existing: !preview.needsCredential,
        role: preview.role,
      });
      // The credentials have done their job. Nothing on this page needs them again, and a password
      // left in state is a password in a React devtools panel.
      setToken("");
      setPassword("");
      setConfirm("");
      redeemClaim.reset();
      previewClaim.reset();
      setStage("done");
    } catch {
      // The connection dropped, or the answer was not the API's. The request may still have been
      // carried out, so the honest advice is to try signing in before trying again.
      setNotice("unconfirmed");
    } finally {
      inFlight.current = false;
    }
  }

  const busy = previewClaim.isPending || redeemClaim.isPending;

  /** The fields a notice is about, so a screen reader that lands on one hears why. A notice that is
   * about no field (an unreachable API, an unconfirmed redemption) marks nothing invalid. */
  const passwordNotice = notice === "mismatch" || notice === "too_weak" ? NOTICE_ID : undefined;
  const nameNotice = notice === "name_missing" || notice === "invalid" ? NOTICE_ID : undefined;

  /** One notice at a time, above the form, as an icon and words. Each is a different fact with a
   * different remedy (see `Notice`), and each keeps the test id the tests and the browser gate read. */
  const message =
    notice === null ? null : (
      <>
        {notice === "refused" ? (
          <AuthNotice id={NOTICE_ID} testId="claim-refused">
            {REFUSED}
          </AuthNotice>
        ) : null}

        {notice === "mismatch" ? (
          <AuthNotice id={NOTICE_ID} testId="join-password-mismatch">
            The two passwords are not the same. Nothing has been sent — type them again.
          </AuthNotice>
        ) : null}

        {notice === "too_weak" ? (
          <AuthNotice id={NOTICE_ID} testId="join-password-too-weak">
            <strong>
              That password is too short — it must be at least {MIN_PASSWORD_LENGTH} characters.
            </strong>{" "}
            Nothing has been sent and your invitation has not been used. Length is what makes a
            password hard to guess; there are no rules about capitals or symbols.
          </AuthNotice>
        ) : null}

        {notice === "name_missing" ? (
          <AuthNotice id={NOTICE_ID} testId="join-name-missing">
            <strong>Type your name.</strong> Nothing has been sent and your invitation has not been
            used.
          </AuthNotice>
        ) : null}

        {notice === "invalid" ? (
          <AuthNotice id={NOTICE_ID} testId="join-invalid">
            <strong>Those details could not be accepted.</strong> Check your name and password — a
            name can be at most {NAME_MAX_LENGTH} characters — and try again. Nothing has been
            changed and your invitation has not been used.
          </AuthNotice>
        ) : null}

        {notice === "unreachable" ? (
          <AuthNotice testId="claim-unreachable">
            <strong>EmitIQ could not be reached.</strong> That does not mean your invitation is
            wrong, and nothing has been changed. Try again in a moment.
          </AuthNotice>
        ) : null}

        {/* **Only here is "try again" not the safe advice.** A redemption that timed out on the
            way back may well have gone through, and an invitation can be used once — so the
            first thing to try is signing in with the password just chosen. */}
        {notice === "unconfirmed" ? (
          <AuthNotice testId="claim-unconfirmed">
            <strong>We could not confirm whether you joined.</strong> The request may have been
            carried out before the connection failed. Try{" "}
            <Link to="/sign-in" search={{ method: "location" }}>
              signing in
            </Link>{" "}
            first. If that does not work, you can try again here; if that is refused too, ask
            whoever invited you to issue a new invitation.
          </AuthNotice>
        ) : null}
      </>
    );

  return (
    <AuthPage>
      {stage === "lookup" ? (
        <AuthCard title="Join a workspace" stage="lookup" headingRef={heading} notice={message}>
          <p className={styles.prose}>
            Enter the invitation token you were given, and the email address it was issued for.
            Nothing about the workspace is shown until both are right.
          </p>
          <form className={styles.form} onSubmit={(event) => void submitLookup(event)}>
            <AuthField label="Invitation token">
              <input
                name="token"
                type="text"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={token}
                disabled={busy}
                onChange={(e) => setToken(e.target.value)}
                aria-invalid={notice === "refused" || undefined}
                aria-describedby={notice === "refused" ? NOTICE_ID : undefined}
                required
              />
            </AuthField>
            <AuthField label="Email address">
              <input
                name="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                disabled={busy}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={notice === "refused" || undefined}
                aria-describedby={notice === "refused" ? NOTICE_ID : undefined}
                required
              />
            </AuthField>
            <button className={styles.primary} type="submit" disabled={busy}>
              {previewClaim.isPending ? "Checking…" : "Continue"}
            </button>
          </form>
          <div className={styles.aside}>
            <p>
              Already joined? <Link to="/sign-in" search={{ method: "location" }}>Sign in</Link>
            </p>
          </div>
        </AuthCard>
      ) : null}

      {stage === "confirm" && preview ? (
        <AuthCard
          title={`You have been invited to join ${preview.organizationName}`}
          stage="confirm"
          headingRef={heading}
          notice={message}
        >
          <dl className={styles.facts}>
            <div>
              <dt>Workspace</dt>
              <dd data-testid="join-organisation">{preview.organizationName}</dd>
            </div>
            <div>
              <dt>Your role</dt>
              <dd data-testid="join-role">{roleWord(preview.role)}</dd>
            </div>
            <div>
              <dt>Email address</dt>
              <dd>{email.trim()}</dd>
            </div>
          </dl>
          <p className={styles.prose}>
            Only continue if you were expecting this invitation from the person who gave you the
            token.
          </p>
          <form className={styles.form} onSubmit={(event) => void submitRedeem(event)}>
            {preview.needsCredential ? (
              <>
                <AuthField label="Your name">
                  <input
                    name="display_name"
                    type="text"
                    autoComplete="name"
                    maxLength={NAME_MAX_LENGTH}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    aria-invalid={nameNotice ? true : undefined}
                    aria-describedby={nameNotice}
                    required
                  />
                </AuthField>
                <AuthField label="Choose a password">
                  <input
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={MIN_PASSWORD_LENGTH}
                    aria-invalid={passwordNotice ? true : undefined}
                    aria-describedby={passwordNotice}
                    required
                  />
                </AuthField>
                <AuthField label="Type the password again">
                  <input
                    name="password_again"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    aria-invalid={notice === "mismatch" || undefined}
                    aria-describedby={notice === "mismatch" ? NOTICE_ID : undefined}
                    required
                  />
                </AuthField>
                <p className={styles.prose}>
                  You choose this password yourself; nobody at EmitIQ sets it for you. It must be
                  at least {MIN_PASSWORD_LENGTH} characters — length is what makes a password hard
                  to guess, and there are no rules about capitals or symbols. The invitation can be
                  used only once, so check it before you continue.
                </p>
              </>
            ) : (
              <p className={styles.prose} data-testid="join-existing">
                There is already an EmitIQ account for this address. Joining adds{" "}
                {preview.organizationName} to it. Your password does not change, and you will sign
                in with it as usual.
              </p>
            )}
            <button className={styles.primary} type="submit" disabled={busy}>
              {redeemClaim.isPending
                ? "Joining…"
                : preview.needsCredential
                  ? "Create my account and join"
                  : `Join ${preview.organizationName}`}
            </button>
            <button
              className={styles.link}
              type="button"
              data-testid="join-change-details"
              onClick={changeDetails}
              disabled={busy}
            >
              Use a different token or address
            </button>
          </form>
        </AuthCard>
      ) : null}

      {stage === "done" && joined ? (
        <AuthCard
          title={`You have joined ${joined.organizationName}`}
          stage="done"
          headingRef={heading}
          notice={message}
        >
          <p className={styles.prose}>
            You are not signed in yet.{" "}
            {joined.existing
              ? "Sign in with the password you already use for EmitIQ."
              : "Sign in with this email address and the password you just chose."}
          </p>
          {joined.existing ? (
            // **Which workspace you land in is not chosen here.** A password resolves to one of
            // the person's workspaces (the first by name) and the switcher moves between them, so
            // an existing person joining a second workspace can arrive in the first and conclude
            // the invitation did nothing. Said before they sign in rather than discovered after.
            <p className={styles.prose} data-testid="join-which-workspace">
              You may arrive in a different workspace first. To move to {joined.organizationName},
              use the <strong>Workspace</strong> menu at the top of the screen.
            </p>
          ) : null}
          {ALWAYS_MFA.has(joined.role) && !joined.existing ? (
            <p className={styles.prose} data-testid="join-mfa-required">
              {withArticle(roleWord(joined.role), { capital: true })} always needs a second factor. After your password you will
              be asked to set up an authenticator app, and the setup has to be finished within five
              minutes — so have the app installed first.
            </p>
          ) : (
            <p className={styles.prose} data-testid="join-mfa-maybe">
              If your role or this workspace&rsquo;s policy needs a second factor, you will be
              asked to set one up as you sign in.
            </p>
          )}
          <Link
            className={styles.primary}
            to="/sign-in"
            search={{ method: "location" }}
            data-testid="join-sign-in"
          >
            Go to sign in
          </Link>
        </AuthCard>
      ) : null}
    </AuthPage>
  );
}
